import React, { useState, useMemo } from "react";
import { Menu, Select } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  BankOutlined,
  AppstoreOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import "@/layout/LayoutStyles/sidebarStyles.css";
import { ArrowLeftIcon, NewBiulding } from "@/asset/icons/icons";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";

import usePermissions from "@/config/usePermissions";
import { normalize, SIDEBAR_STRUCTURE } from "@/config/permissionConfig";
import useAuth from "@/hooks/useAuth";
import { useGetCompanyQuery } from "@/services/api/company/companyApi";
import { Box } from "@mui/material";

// Icon mapping
const ICON_MAP = {
  DashboardOutlined: <DashboardOutlined />,
  ShoppingCartOutlined: <ShoppingCartOutlined />,
  UserOutlined: <UserOutlined />,
  BankOutlined: <BankOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  CreditCardOutlined: <CreditCardOutlined />,
};

const Sidebar = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuth();
  // console.log("user", user);
  const { data: companyData } = useGetCompanyQuery();
  const companyName = companyData?.[0]?.name || "Sundry Food Services";
  const companyLogo = companyData?.[0]?.url;

  const {
    data: locations = [],
    // isLoading: locationLoading
  } = useGetLocationsQuery();
  const [openKeys, setOpenKeys] = useState([]);

  // Get user permissions
  const { normalizedPermissionNames, hasAnyPermissions } = usePermissions();

  /**
   * Build dynamic menu items based on user permissions
   */
  const menuItems = useMemo(() => {
    if (!hasAnyPermissions) return [];

    const items = [];

    // Sort categories by order
    const sortedCategories = Object.entries(SIDEBAR_STRUCTURE).sort(
      ([, a], [, b]) => a.order - b.order,
    );

    for (const [categoryKey, category] of sortedCategories) {
      const categoryChildren = [];

      // Sort modules by order
      const sortedModules = Object.entries(category.modules).sort(
        ([, a], [, b]) => a.order - b.order,
      );

      for (const [moduleName, module] of sortedModules) {
        // Check if user has any permission for this module
        const hasModuleAccess = module.permissions.some((perm) =>
          normalizedPermissionNames.includes(normalize(perm)),
        );

        if (!hasModuleAccess) continue;

        if (module.standalone) {
          // Standalone item (Dashboard, Transactions, Customers)
          categoryChildren.push({
            key: module.path,
            icon: ICON_MAP[module.icon],
            label: moduleName,
          });
        } else {
          // Module with children
          const accessibleChildren = module.children
            .filter((child) =>
              normalizedPermissionNames.includes(normalize(child.permission)),
            )
            .map((child) => ({
              key: child.path,
              label: child.label,
            }));

          // Only add module if it has accessible children
          if (accessibleChildren.length > 0) {
            categoryChildren.push({
              key: moduleName.toLowerCase().replace(/\s+/g, "-"),
              icon: ICON_MAP[module.icon],
              label: moduleName,
              expandIcon: ({ isOpen }) => (
                <ArrowLeftIcon
                  className={`transition-transform duration-200 ${
                    isOpen ? "rotate-90" : "rotate-0"
                  }`}
                />
              ),
              children: accessibleChildren,
            });
          }
        }
      }

      // Only add category if it has children
      if (categoryChildren.length > 0) {
        items.push({
          key: categoryKey.toLowerCase(),
          type: "group",
          label: category.label,
          children: categoryChildren,
        });
      }
    }

    return items;
  }, [normalizedPermissionNames, hasAnyPermissions]);

  const handleMenuClick = ({ key }) => {
    if (key.startsWith("/admin")) {
      navigate(key);
    }
  };

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const getSelectedKeys = () => {
    return [location.pathname];
  };

  // Don't render sidebar if user has no permissions
  if (!hasAnyPermissions) {
    return null;
  }

  return (
    <aside className="w-64 bg-white border-r border-gray-100 h-screen flex flex-col pb-6">
      {/* Logo */}
      <div className="flex gap-2 pt-2 max-h-16 h-16 border-b border-gray-100 px-6">
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            // border: "2px dashed #ccc",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <img
            src={companyLogo}
            alt="Company Logo"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
        <div className="my-auto">
          <h2 className="text-sm font-semibold text-gray-900 mb-0">
            {companyName}
          </h2>
          <p className="text-xs text-gray-500 font-semibold">
            {user?.roles?.map((role) => role.name).join(", ")}
          </p>
        </div>
      </div>

      <div className="px-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] flex-1">
        {/* Location Selector */}
        <div className="p-2 flex gap-2 bg-[#F5F6F9] rounded-lg border border-[#E9EAEB] my-4">
          <div className="my-auto">
            <NewBiulding color="#858A92" />
          </div>
          {/* <Select
            value={user?.location_id}
            onChange={(locationId) => {
              // Handle location change
            }}
            style={{ width: "100%" }}
            placeholder="Select Location"
            options={locations.map((loc) => ({
              label: `${loc.name} - ${loc.state.name}`,
              value: loc.id,
            }))}
          /> */}
          <div className="text-sm text-[#71717B] w-full">
            <span>Location</span>
            <div>
              {locations.find((loc) => loc.id === user?.location_id)
                ? `${
                    locations.find((loc) => loc.id === user?.location_id).name
                  } - ${
                    locations.find((loc) => loc.id === user?.location_id).state
                      .name
                  }`
                : ""}
            </div>
          </div>
        </div>

        {/* Dynamic Menu */}
        <div className="flex-1 mb-4">
          <Menu
            mode="inline"
            selectedKeys={getSelectedKeys()}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onClick={handleMenuClick}
            className="border-none custom-sidebar-menu"
            items={menuItems}
            theme="light"
          />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
